import { useContext } from "react"
import { Menu, MenuButton, MenuList, MenuItem } from "@reach/menu-button"
import { FiltersContext } from "../contexts/FiltersContext"
import { Campus, useAvailableCampuses } from "../hooks/useCampuses"

type MenuListProps = {
  style?: React.CSSProperties
  children: React.ReactNode
}

export function CampusFilter() {
  const campuses = useAvailableCampuses()
  if (campuses.length === 0) return null

  return <CampusMultiSelect campuses={campuses} />
}

function CampusMultiSelect({ campuses }: { campuses: Campus[] }) {
  const title = useTitle(campuses)

  return (
    <Menu>
      <MenuButton className="select truncate ta-l">{title}</MenuButton>
      <ScrollableMenuList>
        {campuses.map((campus) => (
          <MultiSelectCampusItem key={campus.id} campus={campus} />
        ))}
      </ScrollableMenuList>
    </Menu>
  )
}

function MultiSelectCampusItem({ campus }: { campus: Campus }) {
  const { campusIds, setCampusIds } = useContext(FiltersContext)
  const isSelected = campusIds.some((id: string) => campus.id === id)
  const inputId = `campus_select_${campus.id}`

  const handleSelect = () => {
    setCampusIds((prevCampusIds: string[]) => {
      // remove unassigned campus ID if present
      const realCampusIds = prevCampusIds.filter((id) => id !== "0")

      // add or remove the selected campus
      const newIds = isSelected
        ? realCampusIds.filter((id) => id !== campus.id)
        : [...realCampusIds, campus.id]

      // append ID for unassigned-campus groups if any are selected
      return newIds.length > 0 ? [...newIds, "0"] : newIds
    })
  }

  return (
    <MenuItem className="dropdown__item" onSelect={handleSelect}>
      <input
        id={inputId}
        type="checkbox"
        className="checkbox"
        checked={isSelected}
      />
      <label
        htmlFor={inputId}
        className="checkbox-label"
        style={{ verticalAlign: "baseline" }}
      >
        {campus.attributes.name}
      </label>
    </MenuItem>
  )
}

function ScrollableMenuList({ style = {}, ...rest }: MenuListProps) {
  const scrollableStyle = {
    ...style,
    maxHeight: "15.5rem",
    maxWidth: "13rem",
    overflow: "auto",
  }
  return <MenuList {...rest} style={scrollableStyle} />
}

function useTitle(campuses: Campus[]) {
  const { campusIds } = useContext(FiltersContext)
  const selectedCampuses = campuses.filter((c) => campusIds.includes(c.id))

  switch (selectedCampuses.length) {
    case 0:
      return "Campus"
    case 1:
      return `Campus: ${selectedCampuses[0].attributes.name}`
    default:
      return `Campus: ${selectedCampuses.length} selected`
  }
}
